import React, {FC} from 'react';

import bgm1 from 'assets/v3/guide/video/bgm-1.webp';
import bgm2 from 'assets/v3/guide/video/bgm-2.webp';
import dubConfig from 'assets/v3/guide/video/dub-config.webp';
import editEntry from 'assets/v3/guide/video/edit-entry.webp';
import editSubtitle from 'assets/v3/guide/video/edit-subtitle.webp';
import exportImg from 'assets/v3/guide/video/export.webp';
import intro from 'assets/v3/guide/video/intro.webp';
import subtitleStyle1 from 'assets/v3/guide/video/subtitle-style-1.webp';
import subtitleStyle2 from 'assets/v3/guide/video/subtitle-style-2.webp';
import translating from 'assets/v3/guide/video/translating.webp';
import ttsConfig from 'assets/v3/guide/video/tts-config.webp';
import upload from 'assets/v3/guide/video/upload.webp';
import voiceToggle1 from 'assets/v3/guide/video/voice-toggle-1.webp';
import voiceToggle2 from 'assets/v3/guide/video/voice-toggle-2.webp';

/** 사용 가이드 「동영상 번역」 탭 한국어 본문(zh.tsx와 요소 구조 1:1).
 * 기능 소개 + 동영상 업로드(AI 더빙/배경 음악/자막 스타일 설정) + 자막·더빙 편집 + 동영상 내보내기 구성.
 * 2026-09-17 AI 번역. */
const KoContent: FC = () => {
  return (
    <>
      <h2>동영상 번역 개요</h2>
      <ul>
        <li>
          <strong>음성 인식 번역</strong>: 자막 없이 사람 목소리만 담긴 동영상(원본 자막이 있는 동영상도 지원)을
          업로드할 수 있어요. 음성을 인식해 외국어로 번역하고, 필요에 따라 자막 추가 여부를 선택할 수 있어요.
        </li>
        <li>
          <strong>자막 인식 번역</strong>: 원본 자막이 있는 동영상 업로드를 지원해요. AI가 자막을 자동으로 번역한 뒤,
          번역된 자막에 외국어 음성으로 더빙해 줘요.
        </li>
        <li>
          <strong>텍스트 제거</strong>: 텍스트가 포함된 동영상을 업로드하면 화면의 텍스트를 인식해 제거해 줘요.
        </li>
      </ul>
      <img alt="동영상 번역 개요" decoding="async" loading="lazy" src={intro} />

      <h2>1단계: 동영상 업로드</h2>
      <p>여기서는 음성 인식 번역을 예로 들어 설명할게요. 필요한 언어를 선택한 뒤 동영상을 업로드하세요.</p>
      <img alt="동영상 업로드 및 언어 선택" decoding="async" loading="lazy" src={upload} />

      <h3>AI 더빙 설정</h3>
      <p>
        AI 더빙은 기본적으로 켜져 있어요. 더빙 옵션 오른쪽 패널을 클릭하면 팝업에서 목소리를 미리 들어보고 마음에 드는
        목소리를 선택할 수 있어요.
      </p>
      <img alt="AI 더빙 설정" decoding="async" loading="lazy" src={dubConfig} />

      <h3>배경 음악</h3>
      <p>
        배경 음악을 켜면 기본적으로 원본 오디오에서 사람 목소리를 제거해요. 내레이션은 걷어내면서 음악과 효과음은 그대로
        살려 줘요. 음악 라이브러리에서 원하는 곡을 직접 선택할 수도 있어요.
      </p>
      <img alt="배경 음악 설정" decoding="async" loading="lazy" src={bgm1} />
      <img alt="음악 라이브러리" decoding="async" loading="lazy" src={bgm2} />

      <h3>자막 스타일 설정</h3>
      <p>필요에 따라 자막을 켜거나 끌 수 있어요. 자막을 켜면 스타일과 위치를 자유롭게 설정할 수 있어요.</p>
      <img alt="자막 스타일 설정" decoding="async" loading="lazy" src={subtitleStyle1} />
      <img alt="자막 위치 설정" decoding="async" loading="lazy" src={subtitleStyle2} />

      <p>업로드가 완료되면 동영상 번역이 시작돼요.</p>
      <img alt="동영상 번역 진행 중" decoding="async" loading="lazy" src={translating} />

      <h2>2단계: 자막 및 더빙 편집</h2>
      <p>
        번역이 끝난 동영상은 바로 미리 보고 다운로드할 수도 있고, 이어서 보정할 수도 있어요. 【편집】 버튼을 클릭하면
        동영상 번역 작업대가 열려요.
      </p>
      <img alt="동영상 편집 작업대 열기" decoding="async" loading="lazy" src={editEntry} />

      <p>
        자막이나 텍스트를 클릭하면 오른쪽에 【자막 편집】 팝업이 열려요. 텍스트 편집 영역에서 선택한 자막의 내용, 글꼴,
        크기, 색상 등을 수정할 수 있어요.
      </p>
      <img alt="자막 편집 팝업" decoding="async" loading="lazy" src={editSubtitle} />

      <h3>더빙 설정</h3>
      <p>
        【더빙 설정】을 클릭하면 자막 더빙 음성을 수정할 수 있어요. 자막 내용이나 더빙 설정을 변경한 뒤 【미리 듣기】를
        클릭하면 수정된 내용으로 음성이 다시 생성되고, 자동 더빙 오디오를 대체해요.
      </p>
      <img alt="더빙 설정" decoding="async" loading="lazy" src={ttsConfig} />

      <p>
        제목 자막을 읽지 않게 하거나 건너뛰고 싶은 문장이 있다면, 해당 자막을 클릭해 음성 자막과 일반 텍스트 상태를
        전환할 수 있어요(자유롭게 왔다 갔다 바꿀 수 있어요).
      </p>
      <img alt="음성 자막과 일반 텍스트 전환" decoding="async" loading="lazy" src={voiceToggle1} />
      <img alt="자막 음성 전환" decoding="async" loading="lazy" src={voiceToggle2} />

      <h2>3단계: 동영상 내보내기</h2>
      <p>
        모두 확인했다면 오른쪽 상단의 【내보내기】 버튼을 클릭하고, 필요한 화질을 선택해 동영상을 내보내 게시에
        활용하세요.
      </p>
      <img alt="동영상 내보내기" decoding="async" loading="lazy" src={exportImg} />
    </>
  );
};

export default KoContent;
